import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environment';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import type { FilesDetailsDto } from '@sharedlib/dtos/dto/files-app/FilesDetailsDto';
import { type Observable } from 'rxjs';

export interface FileVersionAnswer {
  readonly item: FilesDetailsDto;
  readonly version: unknown;
}
export interface FileVersion {
  readonly version: number;
  readonly kind: string;
  readonly author: { readonly id: string; readonly name: string | null } | null;
  readonly at: string;
  readonly restoredFrom: number | null;
  readonly review: 'requested' | 'approved' | 'changes_requested' | null;
}
export interface FileVersionsPage {
  readonly items: readonly FileVersion[];
  readonly current: number | null;
  readonly total: number;
}
/** One saved version, with a signed link to exactly its bytes. */
export interface SavedFileVersion extends FileVersion {
  readonly attachmentName: string;
  readonly signedUrl: string;
}
export interface ReplaceFileVersionBody {
  readonly tempAttachmentId: string;
  readonly attachmentName: string;
}

/**
 * files-app's version routes, always under the file's own workspace. Provided per component, never at root: only the
 * space-context subtree's HttpClient carries the spaces interceptor that turns the owning-tenant context into the
 * request's space_id; the root client would send none and the gateway refuses it (#2074).
 */
@Injectable()
export class FileVersionsClient {
  private readonly http = inject(HttpClient);

  public replace(file: { id: string; accountId: string }, body: ReplaceFileVersionBody): Observable<FileVersionAnswer> {
    return this.http.post<FileVersionAnswer>(this.url(file), body, { context: owningTenantContext(file.accountId) });
  }
  public list(file: { id: string; accountId: string }, offset: number): Observable<FileVersionsPage> {
    return this.http.get<FileVersionsPage>(this.url(file), {
      params: new HttpParams().set('offset', offset).set('limit', 25),
      context: owningTenantContext(file.accountId),
    });
  }
  public read(file: { id: string; accountId: string }, version: number): Observable<SavedFileVersion> {
    return this.http.get<SavedFileVersion>(`${this.url(file)}/${version}`, { context: owningTenantContext(file.accountId) });
  }
  public restore(file: { id: string; accountId: string }, version: number): Observable<FileVersionAnswer> {
    return this.http.post<FileVersionAnswer>(`${this.url(file)}/${version}/restore`, null, {
      context: owningTenantContext(file.accountId),
    });
  }
  // FilesService's own url (`/api/files/files/`); it keeps that field protected.
  private url(file: { id: string }): string {
    return `${environment.baseUrl}/api/files/files/${encodeURIComponent(file.id)}/versions`;
  }
}

export function fileVersionError(error: unknown, fallback: string): string {
  if (error instanceof HttpErrorResponse) {
    const body: unknown = error.error;
    if (body && typeof body === 'object' && 'message' in body && typeof body.message === 'string') return body.message;
  }
  return fallback;
}
